import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from "react";
import MainLayout from "../components/mainLayout";
import { api } from "../api";
import "../sass/pages/pontoScreen.scss";
import { showInfo, showError } from "../utils/toast";

/* ---------- utils ---------- */
const fmt2 = (n) => String(n).padStart(2, "0");
const hhmmss = (d) =>
  `${fmt2(d.getHours())}:${fmt2(d.getMinutes())}:${fmt2(d.getSeconds())}`;
const LOCAL_SKEW_VISIBILITY_MS = 60_000;

const getStatusPonto = (diaObj) => {
  if (!diaObj?.dados) return "Falta";
  const { EntradaManha, SaidaManha, EntradaTarde, SaidaTarde } = diaObj.dados;
  return EntradaManha && SaidaManha && EntradaTarde && SaidaTarde
    ? "Completo"
    : "Pendente";
};

const getProximoCampo = (dados) => {
  if (!dados?.EntradaManha) return "EntradaManha";
  if (!dados?.SaidaManha) return "SaidaManha";
  if (!dados?.EntradaTarde) return "EntradaTarde";
  if (!dados?.SaidaTarde) return "SaidaTarde";
  if (!dados?.EntradaExtra) return "EntradaExtra";
  if (!dados?.SaidaExtra) return "SaidaExtra";
  return null;
};

const formatCampoLabel = (campo) =>
  String(campo || "")
    .replace(/([A-Z])/g, " $1")
    .trim();

const getHorarioPrevisto = (horarios, campo) => {
  if (!horarios || !campo) return null;

  const mapa = {
    EntradaManha: horarios.EntradaManha,
    SaidaManha: horarios.SaidaManha,
    EntradaTarde: horarios.EntradaTarde,
    SaidaTarde: horarios.SaidaTarde,
  };

  return mapa[campo] || null;
};

/* ---------- componente ---------- */
const PontoScreen = () => {
  const codFunc = Number(sessionStorage.getItem("codFunc"));
  const codCadFunc = Number(sessionStorage.getItem("CodCadFunc"));
  const idFuncionario = Number(sessionStorage.getItem("IdFuncionario"));
  const nomeFunc = sessionStorage.getItem("RazaoSocial") || "Funcionário";

  const LS_KEY = `PONTO_COMP_${codFunc}`;

  const ls = (() => {
    try {
      return JSON.parse(localStorage.getItem(LS_KEY) || "null");
    } catch {
      return null;
    }
  })();

  /* dataset */
  const [catalogoMeses, setCatalogoMeses] = useState([]);
  const [dadosPonto, setDadosPonto] = useState([]);
  const [loading, setLoading] = useState(true);
  const [horarios, setHorarios] = useState(null);
  const [registrando, setRegistrando] = useState(false);
  const inFlightRef = useRef(false);

  /* relógio do servidor */
  const [serverClock, setServerClock] = useState({
    now: null,
    skewMs: 0,
    lastSync: null,
    healthy: false,
    tzMinutes: 0,
    source: null,
  });

  /* referência do "agora" baseada no servidor */
  const nowRef = serverClock.now || new Date();
  const mmNow = fmt2(nowRef.getMonth() + 1);
  const yyNow = String(nowRef.getFullYear());

  const [mesSel, setMesSel] = useState(ls?.mes || mmNow);
  const [anoSel, setAnoSel] = useState(ls?.ano || yyNow);

  /* helpers de horário com TZ do servidor */
  const hhmmssTZ = (epochMsUTC, tzMinutes = 0) => {
    const d = new Date(epochMsUTC);
    let total = d.getUTCHours() * 60 + d.getUTCMinutes() + tzMinutes;
    total = ((total % 1440) + 1440) % 1440;
    const h = Math.floor(total / 60);
    const m = total % 60;
    return `${fmt2(h)}:${fmt2(m)}:${fmt2(d.getUTCSeconds())}`;
  };

  const hhmmTZ = (epochMsUTC, tzMinutes = 0) =>
    hhmmssTZ(epochMsUTC, tzMinutes).slice(0, 5);

  const tzLabel = (min) => {
    const sign = (min ?? 0) >= 0 ? "+" : "-";
    const abs = Math.abs(min ?? 0);
    return `UTC${sign}${fmt2(Math.floor(abs / 60))}:${fmt2(abs % 60)}`;
  };

  /* ---------- sync hora servidor ---------- */
  const syncServerTime = useCallback(async () => {
    try {
      const { data } = await api.get("/ponto/server-time");

      const serverUtcMs =
        (Number.isFinite(data?.epochMsUtc) && data.epochMsUtc) ||
        (data?.serverUTCISO && Date.parse(data.serverUTCISO)) ||
        (data?.serverOffsetISO && Date.parse(data.serverOffsetISO)) ||
        null;

      if (!serverUtcMs) throw new Error("payload inesperado");

      setServerClock({
        now: new Date(serverUtcMs),
        skewMs: serverUtcMs - Date.now(),
        lastSync: new Date(),
        healthy: true,
        tzMinutes: Number(data?.offsetMinutes) || 0,
        source: data?.source || "sqlserver",
      });
    } catch (e) {
      console.warn("Falha ao sincronizar hora do servidor.", e);
      setServerClock((s) => ({
        ...s,
        now: new Date(),
        skewMs: 0,
        lastSync: new Date(),
        healthy: false,
      }));
    }
  }, []);

  useEffect(() => {
    syncServerTime();

    const tick = setInterval(() => {
      setServerClock((s) =>
        s.now ? { ...s, now: new Date(s.now.getTime() + 1000) } : s
      );
    }, 1000);

    const resync = setInterval(syncServerTime, 60_000);

    return () => {
      clearInterval(tick);
      clearInterval(resync);
    };
  }, [syncServerTime]);

  /* garante que o seletor acompanhe o mês corrente do servidor na primeira carga */
  useEffect(() => {
    if (!ls && serverClock.now) {
      setMesSel((prev) => prev || mmNow);
      setAnoSel((prev) => prev || yyNow);
    }
  }, [ls, serverClock.now, mmNow, yyNow]);

  /* ---------- backend fetch ---------- */
  const fetchCatalogo = useCallback(async () => {
    const { data } = await api.get(`/ponto/meses/${codFunc}`);

    const exists = data.some(
      (m) => m.ano === Number(yyNow) && String(m.mes) === String(mmNow)
    );

    const extra = exists
      ? []
      : [
          {
            ano: Number(yyNow),
            mes: mmNow,
            mesRef: `${yyNow}-${mmNow}`,
            qtdRegistros: 0,
          },
        ];

    setCatalogoMeses(
      [...data, ...extra].sort((a, b) =>
        a.ano !== b.ano ? b.ano - a.ano : String(b.mes).localeCompare(String(a.mes))
      )
    );
  }, [codFunc, mmNow, yyNow]);

  const fetchHorarios = useCallback(async () => {
    try {
      if (Number.isFinite(idFuncionario) && idFuncionario > 0) {
        const { data } = await api.get(`/func-horario/func/${idFuncionario}/horario`);
        setHorarios(data || null);
        return;
      }
    } catch (e) {
      console.warn("Falha ao buscar horário pela rota nova. Tentando fallback.", e);
    }

    try {
      const { data } = await api.get(`/inform-func/${codFunc}`);
      setHorarios(data || null);
    } catch (e) {
      console.warn("Falha ao buscar horário do funcionário.", e);
      setHorarios(null);
    }
  }, [codFunc, idFuncionario]);

  const fetchMes = useCallback(
    async (mes, ano) => {
      setLoading(true);
      try {
        const { data } = await api.get(`/ponto/${codFunc}?mes=${mes}&ano=${ano}&limit=500`);
        setDadosPonto(data?.data || []);
      } catch (e) {
        console.error("[ERRO] Falha ao buscar ponto:", e);
        showError("Falha ao carregar os registros.");
      } finally {
        setLoading(false);
      }
    },
    [codFunc]
  );

  useEffect(() => {
    fetchCatalogo();
  }, [fetchCatalogo]);

  useEffect(() => {
    fetchHorarios();
  }, [fetchHorarios]);

  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify({ mes: mesSel, ano: anoSel }));
    fetchMes(mesSel, anoSel);
  }, [mesSel, anoSel, fetchMes, LS_KEY]);

  /* ---------- referência do dia atual pelo relógio do servidor ---------- */
  const diaHojeRef = useMemo(() => {
    const d = serverClock.now || new Date();
    return {
      d: fmt2(d.getDate()),
      m: fmt2(d.getMonth() + 1),
      y: String(d.getFullYear()),
    };
  }, [serverClock.now]);

  const pontoHoje = useMemo(() => {
    if (mesSel !== diaHojeRef.m || anoSel !== diaHojeRef.y) return null;

    return (
      dadosPonto.find(
        (p) =>
          fmt2(Number(p.Dia)) === diaHojeRef.d &&
          fmt2(Number(p.Mes)) === diaHojeRef.m &&
          String(p.Ano) === diaHojeRef.y
      ) || null
    );
  }, [dadosPonto, mesSel, anoSel, diaHojeRef]);

  const proximoCampoHoje = getProximoCampo(pontoHoje || {});
  const proximoHorarioPrevisto = getHorarioPrevisto(horarios, proximoCampoHoje);

  const handleRegistrarPonto = async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setRegistrando(true);

    try {
      const { data } = await api.post("/ponto/bater", {
        NomeFunc: nomeFunc,
        CodFunc: codFunc,
        CodCadFunc: codCadFunc,
        strictHorario: false,
      });

      const campoGravado = data?.campoGravado;
      const foraJanela = data?.horarioValidacao?.foraDaJanela;

      if (campoGravado) {
        const baseMsg = `${formatCampoLabel(campoGravado)} registrado com sucesso.`;

        if (foraJanela) {
          showInfo(`${baseMsg} Fora da janela prevista.`);
        } else {
          showInfo(baseMsg);
        }
      } else {
        showInfo("Ponto registrado com sucesso.");
      }

      await fetchMes(mesSel, anoSel);
      await fetchCatalogo();
    } catch (e) {
      const msg =
        e?.response?.data?.mensagem ||
        e?.response?.data?.message ||
        "Erro ao registrar ponto.";
      showError(msg);
    } finally {
      setRegistrando(false);
      inFlightRef.current = false;
    }
  };

  /* ---------- calendário do mês selecionado ---------- */
  const diasDaSemana = ["dom.", "seg.", "ter.", "qua.", "qui.", "sex.", "sáb."];

  const diasDoMes = useMemo(() => {
    const dias = [];
    const year = Number(anoSel);
    const monthIndex = Number(mesSel) - 1;
    const totalDias = new Date(year, monthIndex + 1, 0).getDate();

    for (let dia = 1; dia <= totalDias; dia++) {
      const data = new Date(year, monthIndex, dia);
      const diaSemanaIndex = data.getDay();
      const dados = dadosPonto.find((p) => Number(p.Dia) === dia);

      dias.push({
        dia,
        semana: diasDaSemana[diaSemanaIndex],
        finalSemana: [0, 6].includes(diaSemanaIndex),
        dados,
        isHoje:
          mesSel === diaHojeRef.m &&
          anoSel === diaHojeRef.y &&
          dia === Number(diaHojeRef.d),
      });
    }

    return dias;
  }, [dadosPonto, mesSel, anoSel, diaHojeRef]);

  /* ---------- UI derivada ---------- */
  const diffLabel = useMemo(() => {
    const ms = Math.abs(serverClock.skewMs);
    const sign = serverClock.skewMs >= 0 ? "+" : "-";
    const s = Math.round(ms / 1000);
    const mm = Math.floor(s / 60);
    const ss = s % 60;
    return `${sign}${fmt2(mm)}:${fmt2(ss)}`;
  }, [serverClock.skewMs]);

  const proximoRotulo = proximoCampoHoje
    ? `Registrar ${formatCampoLabel(proximoCampoHoje)}`
    : "Registrar (completo)";

  const horaMostrada = serverClock.now
    ? hhmmssTZ(serverClock.now.getTime(), serverClock.tzMinutes ?? 0)
    : hhmmssTZ(Date.now(), serverClock.tzMinutes ?? 0);

  const vaiRegistrarAs = proximoHorarioPrevisto
    ? proximoHorarioPrevisto
    : serverClock.now
    ? hhmmTZ(serverClock.now.getTime(), serverClock.tzMinutes ?? 0)
    : hhmmTZ(Date.now(), serverClock.tzMinutes ?? 0);

  const showLocalClock =
    !serverClock.healthy ||
    Math.abs(serverClock.skewMs) >= LOCAL_SKEW_VISIBILITY_MS;

  return (
    <MainLayout>
      <div className="ponto-container">
        <div className="header-row">
          <h1>Cartão de Ponto</h1>

          <div className="competencia">
            <label>Competência</label>
            <select
              value={`${anoSel}-${mesSel}`}
              onChange={(e) => {
                const [a, m] = e.target.value.split("-");
                setAnoSel(a);
                setMesSel(m);
              }}
            >
              {catalogoMeses.map((m) => (
                <option key={`${m.ano}-${m.mes}`} value={`${m.ano}-${m.mes}`}>
                  {m.mes}/{m.ano}
                </option>
              ))}
            </select>

            <button
              className="btn-small"
              onClick={() => {
                setMesSel(mmNow);
                setAnoSel(yyNow);
              }}
              title="Ir para o mês atual"
            >
              Hoje
            </button>

            <button
              className="btn-small"
              onClick={() => {
                fetchMes(mesSel, anoSel);
                fetchCatalogo();
                fetchHorarios();
              }}
            >
              Recarregar
            </button>
          </div>
        </div>

        <div className="server-clock card">
          <div className="server-clock__left">
            <div className="server-clock__times">
              <div>
                <div className="server-clock__label">
                  Horário do servidor
                  <span className="tz">{tzLabel(serverClock.tzMinutes)}</span>
                </div>
                <div className="server-clock__time">{horaMostrada}</div>
              </div>

              {showLocalClock && (
                <div className="server-clock__local">
                  <div className="server-clock__label muted">Seu computador</div>
                  <div className="server-clock__time--small">
                    {hhmmss(new Date())}
                  </div>
                </div>
              )}
            </div>

            <div className="server-clock__sub">
              {serverClock.healthy ? (
                <>
                  sincronizado há{" "}
                  {serverClock.lastSync
                    ? `${Math.max(
                        0,
                        Math.floor(
                          (Date.now() - serverClock.lastSync.getTime()) / 1000
                        )
                      )}s`
                    : "-"}
                  {" · "}
                  <span className="pill">diferença {diffLabel}</span>
                  {serverClock.source ? ` · fonte: ${serverClock.source}` : ""}
                  {" · gravação usa o servidor"}
                </>
              ) : (
                <span className="warn">
                  sem sincronismo — gravação usa o servidor
                </span>
              )}
            </div>
          </div>

          <div className="server-clock__right">
            <div className="next-action">
              <div className="next-action__title">Próximo registro</div>

              <div className="next-action__row">
                <span className="next-action__tag">
                  {proximoCampoHoje ? formatCampoLabel(proximoCampoHoje) : "—"}
                </span>
                <span className="muted">às</span>
                <span className="next-action__time">{vaiRegistrarAs}</span>
              </div>

              <button
                type="button"
                className={`btn-registrar${registrando ? " is-loading" : ""}`}
                onClick={handleRegistrarPonto}
                disabled={
                  registrando ||
                  !diaHojeRef ||
                  mesSel !== diaHojeRef.m ||
                  anoSel !== diaHojeRef.y ||
                  !proximoCampoHoje
                }
                aria-busy={registrando ? "true" : "false"}
              >
                {registrando ? "Registrando..." : proximoRotulo}
              </button>

              {mesSel !== diaHojeRef.m || anoSel !== diaHojeRef.y ? (
                <div className="hint muted" style={{ marginTop: 6 }}>
                  Para registrar, selecione o mês atual do servidor.
                </div>
              ) : null}

              {!proximoCampoHoje ? (
                <div className="hint muted" style={{ marginTop: 6 }}>
                  Todos os pontos de hoje já foram registrados.
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {loading ? (
          <p>Carregando dados...</p>
        ) : (
          <div className="tabela-scroll">
            <table className="tabela-ponto">
              <thead>
                <tr>
                  <th>Dia</th>
                  <th>Semana</th>
                  <th>Status</th>
                  <th>Entrada Manhã</th>
                  <th>Saída Manhã</th>
                  <th>Entrada Tarde</th>
                  <th>Saída Tarde</th>
                  <th>Entrada Extra</th>
                  <th>Saída Extra</th>
                </tr>
              </thead>
              <tbody>
                {diasDoMes.map((d, idx) => (
                  <tr key={idx} className={d.isHoje ? "is-today" : ""}>
                    <td>
                      {fmt2(d.dia)}/{mesSel}
                    </td>
                    <td>{d.semana}</td>
                    <td>
                      <span
                        className={
                          "status-badge " +
                          (getStatusPonto(d) === "Completo"
                            ? "ok"
                            : getStatusPonto(d) === "Pendente"
                            ? "pending"
                            : "neutral")
                        }
                      >
                        {getStatusPonto(d)}
                      </span>
                    </td>
                    <td>{d.dados?.EntradaManha || "-"}</td>
                    <td>{d.dados?.SaidaManha || "-"}</td>
                    <td>{d.dados?.EntradaTarde || "-"}</td>
                    <td>{d.dados?.SaidaTarde || "-"}</td>
                    <td>{d.dados?.EntradaExtra || "-"}</td>
                    <td>{d.dados?.SaidaExtra || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default PontoScreen;